import { useAuth0 } from '@auth0/auth0-react';
import { AlertV2, ButtonV2, Checkbox, InputV2 } from '@cimplico/components';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCreateFirm } from '../../../../queries/use-create-firm';
import { Firm } from '../../../../types/firm';
import OnboardingStep from '../../OnboardingStep';

type FirmDetailsProps = {
    onNext: (firm: Firm) => void;
    onJoin: () => void;
};

type FormType = {
    name: string;
    teamSize: string;
    primaryWork: string;
};

function FirmDetails({ onNext, onJoin }: FirmDetailsProps): JSX.Element {
    const { getAccessTokenSilently } = useAuth0();
    const [globalError, setGlobalError] = useState<string>();
    const [refreshingTokens, setRefreshingTokens] = useState(false);
    const [termsAgree, setTermsAgree] = useState(false);

    const form = useForm({
        defaultValues: {
            name: '',
            teamSize: '1-10',
            primaryWork: 'Accounting',
        },
    });
    const createFirm = useCreateFirm(form.setError);

    function handleSubmit(data: FormType): void {
        createFirm.mutate(data, {
            onSuccess: async ({ firm }) => {
                setRefreshingTokens(true);
                await getAccessTokenSilently({ detailedResponse: true, cacheMode: 'off' });
                setRefreshingTokens(false);
                onNext(firm);
            },
            onError: (error) => {
                // Any errors without a  source needs to be displayed in a banner
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const globalErrors = error.response?.data?.errors.filter((e: any) => !e.source);
                if (globalErrors[0]) {
                    setGlobalError(globalErrors[0].detail);
                }
            },
        });
    }

    return (
        <OnboardingStep
            description="Tell us a bit more about your firm so we can provide you a personalised experience tailored to you."
            title="Let's set up your firm"
        >
            <form className="flex flex-col gap-4 max-w-[600px]" {...form} onSubmit={form.handleSubmit(handleSubmit)}>
                {globalError && <AlertV2 type="error" title={globalError} />}
                {/* Firm name */}
                <Controller
                    name="name"
                    control={form.control}
                    rules={{}}
                    render={({ field, fieldState }): JSX.Element => {
                        return (
                            <InputV2
                                type="text"
                                placeholder="ABC Accounting"
                                title="Firm name"
                                autoFocus
                                onChange={field.onChange}
                                errorMessage={fieldState.error?.message}
                            />
                        );
                    }}
                />

                {/* Team size */}
                <div>
                    <label className="block text-gray-700 font-medium text-sm">Team size</label>
                    <select
                        {...form.register('teamSize')}
                        className="w-full rounded-md border h-[34px] text-sm leading-4 text-gray-800 border-gray-200 ring-0 outline-0 focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                        <option>1-10</option>
                        <option>11-25</option>
                        <option>26-50</option>
                        <option>51-100</option>
                        <option>101+</option>
                    </select>
                </div>
                {/* Primary Work */}
                <div>
                    <label className="block text-gray-700 font-medium text-sm">Primary work</label>
                    <select
                        {...form.register('primaryWork')}
                        className="w-full rounded-md border h-[34px] text-sm leading-4 text-gray-800 border-gray-200 ring-0 outline-0 focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                        <option value="Accounting">🧮 Accounting</option>
                        <option value="Bookkeeping">📚 Bookkeeping</option>
                        <option value="Financial Planning">🏦 Financial planning</option>
                        <option value="Self Managed Super Funds">🌱 Self managed super funds</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
                {/* Terms */}
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="terms"
                        onChange={(e): void => {
                            setTermsAgree(e.target.checked);
                        }}
                    />
                    <label className="text-sm text-gray-600" htmlFor="terms">
                        I agree to the{' '}
                        <a
                            className="text-indigo-600 font-medium"
                            href="https://www.cimplico.com/terms-conditions/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            terms and conditions
                        </a>
                    </label>
                </div>

                <ButtonV2
                    size="sm"
                    label="Start Trial"
                    isSubmit
                    isDisabled={!termsAgree || createFirm.isLoading || refreshingTokens}
                    isLoading={createFirm.isLoading || refreshingTokens}
                />
            </form>
            <div className="flex items-center mt-8">
                <p className="text-contentSecondary text-sm">Looking to join an existing firm?</p>
                <button className="text-primary text-sm ml-2" onClick={onJoin}>
                    See my available firms
                </button>
            </div>
        </OnboardingStep>
    );
}

export default FirmDetails;
